import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sun, IndianRupee, CalendarClock, Leaf } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Reveal, SectionHeading } from "@/components/landing/Reveal";
import { useQuote } from "@/context/QuoteContext";

const CITIES = ["Hyderabad", "Warangal", "Karimnagar", "Nizamabad", "Khammam"];

const OutputBar = ({ label, value, max, color, formatted }) => {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-500">{label}</span>
        <span className="text-sm font-bold text-brand-navy">{formatted}</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
    </div>
  );
};

const Calculator = () => {
  const { openQuote } = useQuote();
  const [bill, setBill] = useState([5000]);
  const [propertyType, setPropertyType] = useState("Residential");
  const [city, setCity] = useState("Hyderabad");

  const result = useMemo(() => {
    const monthlyBill = bill[0];
    const unitRate = propertyType === "Commercial" ? 9 : 7.5;
    const units = monthlyBill / unitRate;
    const kW = Math.max(1, units / 120);
    const monthlySavings = Math.round(monthlyBill * 0.9);
    const costPerKw = propertyType === "Commercial" ? 55000 : 62000;
    const systemCost = kW * costPerKw;
    const subsidy = propertyType === "Residential" ? Math.min(78000, kW * 18000) : 0;
    const netCost = Math.max(systemCost - subsidy, 0);
    const paybackYears = Math.max(2.5, netCost / (monthlySavings * 12));
    const co2 = kW * 1.2;
    return {
      kW: kW.toFixed(1),
      monthlySavings,
      yearlySavings: monthlySavings * 12,
      paybackYears: paybackYears.toFixed(1),
      co2: co2.toFixed(1),
    };
  }, [bill, propertyType]);

  return (
    <section id="calculator" className="py-24 md:py-32" data-testid="calculator-section">
      <div className="max-content mx-auto px-4 md:px-8 lg:px-12">
        <Reveal className="flex justify-center text-center">
          <SectionHeading
            eyebrow="Savings Calculator"
            title="See How Much You Could Save"
            subtitle="Move the slider to estimate your solar savings, payback period and carbon offset in seconds."
            className="mx-auto"
          />
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 grid lg:grid-cols-2 gap-6 rounded-[28px] bg-white border border-slate-100 shadow-glass p-6 md:p-10">
            {/* Inputs */}
            <div className="space-y-8">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-brand-navy">Monthly Electricity Bill</label>
                  <span className="text-lg font-bold text-brand-green">₹{bill[0].toLocaleString("en-IN")}</span>
                </div>
                <Slider
                  value={bill}
                  onValueChange={setBill}
                  min={500}
                  max={50000}
                  step={500}
                  data-testid="calc-bill-slider"
                />
                <div className="flex justify-between mt-2 text-xs text-slate-400">
                  <span>₹500</span><span>₹50,000</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-brand-navy">Property Type</label>
                  <Select value={propertyType} onValueChange={setPropertyType}>
                    <SelectTrigger data-testid="calc-type-select" className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-brand-navy">Location</label>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger data-testid="calc-city-select" className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-2xl bg-brand-green/5 border border-brand-green/20 p-4">
                <Sun className="h-5 w-5 text-brand-green shrink-0" />
                <p className="text-sm text-slate-600">
                  Recommended system size: <span className="font-bold text-brand-navy" data-testid="calc-system-size">{result.kW} kW</span>
                </p>
              </div>
            </div>

            {/* Outputs */}
            <div className="rounded-3xl bg-brand-navy p-6 md:p-8 text-white flex flex-col justify-between">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-white/10 p-4">
                    <IndianRupee className="h-5 w-5 text-brand-yellow mb-2" />
                    <p className="text-xs text-slate-300">Est. Monthly Savings</p>
                    <p className="text-2xl font-bold" data-testid="calc-monthly-savings">₹{result.monthlySavings.toLocaleString("en-IN")}</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4">
                    <CalendarClock className="h-5 w-5 text-brand-yellow mb-2" />
                    <p className="text-xs text-slate-300">Payback Period</p>
                    <p className="text-2xl font-bold" data-testid="calc-payback">{result.paybackYears} yrs</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <OutputBar label="Yearly Savings" value={result.yearlySavings} max={540000} color="#FFC928" formatted={`₹${result.yearlySavings.toLocaleString("en-IN")}`} />
                  <OutputBar label="CO₂ Reduction / year" value={Number(result.co2)} max={40} color="#4CAF50" formatted={`${result.co2} Tons`} />
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Leaf className="h-4 w-4 text-brand-green" />
                  Equivalent to planting {Math.round(Number(result.co2) * 45)} trees a year.
                </div>
              </div>

              <Button
                onClick={() => openQuote("calculator")}
                data-testid="calc-cta-survey"
                className="mt-6 h-13 py-3.5 rounded-full bg-brand-yellow text-brand-navy font-semibold text-base hover:bg-[#f0bd1f] hover:-translate-y-0.5 transition-transform"
              >
                Book Free Site Survey
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Calculator;
